import React from 'react';
import {
  IconButton, Menu, ConfirmDialog, Spinner,
} from '@parsina/framework';
import {
  ArrowRight, PanelLeftClose, PanelLeftOpen, MoreHorizontal, Pencil, Trash2,
} from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { PracticeBadge, AiDisclaimer } from '../../components/common/PracticeNotice.jsx';
import ErrorState from '../../components/common/ErrorState.jsx';
import ThreadSidebar, { SIDEBAR_WIDTH } from '../../components/chat/ThreadSidebar.jsx';
import MessageList from '../../components/chat/MessageList.jsx';
import SuggestedPrompts from '../../components/chat/SuggestedPrompts.jsx';
import FollowUps from '../../components/chat/FollowUps.jsx';
import Composer from '../../components/chat/Composer.jsx';
import { readDraft, writeDraft, CHAT_SIDEBAR_KEY } from '../../lib/drafts.js';
import { threadTitle } from '../../lib/conversation.js';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { canAfford } from '../../components/common/ParsehCost.jsx';
import { sidebarOpenFrom, sidebarOpenTo } from './Chat.shared.js';

// The desktop chat. core.md §9.1.
//
// A 260px conversation rail that COLLAPSES (its state in
// `localStorage.chat_sidebar_v1`, so it is the same on the next visit), and a
// main column of: header, the standing AI disclaimer, the transcript,
// the follow-up chips, the composer.
//
// The disclaimer does not collapse and cannot be dismissed. A safety notice with
// an × is one that is dismissed in the first session and never seen again, and
// this one is the sentence that separates a teaching answer from a clinical one
// (core.md §3.1).

export default function ChatWeb({
  threadId, name, caseRecord, caseLoading, caseError, reloadCase,
  balance, chat, send, openThread, startNew, rename, remove,
  focusSignal, offline, onBack, onTopUp, onSwitchPatient, onOpenVisits,
}) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = React.useState(() => sidebarOpenFrom(readDraft(CHAT_SIDEBAR_KEY, { kind: 'local' })));
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [titleValue, setTitleValue] = React.useState('');
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const menuAnchor = React.useRef(null);

  const toggleSidebar = () => {
    setSidebarOpen((open) => {
      writeDraft(CHAT_SIDEBAR_KEY, sidebarOpenTo(!open), { kind: 'local' });
      return !open;
    });
  };

  const title = threadTitle(chat.thread, chat.pendingTitle || t('chat.thread.untitled'));
  const empty = !chat.loading && chat.messages.length === 0;
  // A KNOWN balance that cannot pay for one question disables the send and
  // shows the top-up card BEFORE the spend (core.md §3.5, §9.2). An unknown
  // balance allows it — the server is the real gate, and it refuses too.
  const outOfCredits = !canAfford(balance, useCreditPrices().costOf('ASK'));

  const commitTitle = async () => {
    setEditingTitle(false);
    if (chat.thread && titleValue.trim() !== title.trim()) {
      await rename(chat.thread.id, titleValue.trim());
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0, background: C.bg }}>
      {sidebarOpen ? (
        <div style={{ width: SIDEBAR_WIDTH, flexShrink: 0, minHeight: 0 }}>
          <ThreadSidebar
            threads={chat.threads}
            loading={chat.threadsLoading}
            activeThreadId={threadId}
            balance={balance}
            onNew={startNew}
            onOpen={openThread}
            onRename={rename}
            onDelete={remove}
            onSwitchPatient={onSwitchPatient}
            onOpenVisits={onOpenVisits}
          />
        </div>
      ) : null}

      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <header
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
            background: C.card,
          }}
        >
          <IconButton
            size={34}
            variant="ghost"
            icon={sidebarOpen ? <PanelLeftClose size={17} aria-hidden /> : <PanelLeftOpen size={17} aria-hidden />}
            label={t(sidebarOpen ? 'chat.sidebar.collapse' : 'chat.sidebar.expand')}
            onClick={toggleSidebar}
            data-testid="sidebar-toggle"
          />
          <IconButton
            size={34}
            variant="ghost"
            icon={<ArrowRight size={17} aria-hidden style={{ transform: 'scaleX(var(--dir-flip, 1))' }} />}
            label={t('chat.header.back')}
            onClick={onBack}
            data-testid="chat-back"
          />

          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            {caseLoading ? <Spinner size={16} label={t('common.labels.loading')} /> : null}
            <span
              data-testid="chat-patient-name"
              style={{
                fontSize: 14.5, fontWeight: 800, color: C.textPrimary,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {name}
            </span>
            {/* A patient here is invented, and no screen that names one may hide it. */}
            <PracticeBadge short />

            {chat.thread ? (
              editingTitle ? (
                <form onSubmit={(e) => { e.preventDefault(); commitTitle(); }} style={{ flex: 1, minWidth: 0 }}>
                  <input
                    autoFocus
                    value={titleValue}
                    aria-label={t('chat.thread.renamePlaceholder')}
                    placeholder={t('chat.thread.renamePlaceholder')}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onBlur={commitTitle}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditingTitle(false); }}
                    data-testid="chat-title-input"
                    style={{
                      width: '100%', maxWidth: 340, font: 'inherit', fontSize: 13,
                      padding: '6px 9px', borderRadius: 8,
                      border: `1px solid ${C.teal}`, background: C.card, color: C.textPrimary,
                    }}
                  />
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => { setTitleValue(title); setEditingTitle(true); }}
                  data-testid="chat-title"
                  title={t('chat.thread.renameTitle')}
                  style={{
                    font: 'inherit', fontSize: 13, color: CT.ink2, cursor: 'text',
                    background: 'transparent', border: 'none', padding: '4px 6px',
                    maxWidth: 340, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {title}
                </button>
              )
            ) : chat.pendingTitle ? (
              // The unsaved conversation's title-to-be, while its first answer
              // is in flight. Not editable: there is nothing to rename yet.
              <span
                data-testid="chat-title"
                style={{
                  fontSize: 13, color: CT.ink2, padding: '4px 6px',
                  maxWidth: 340, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {chat.pendingTitle}
              </span>
            ) : null}
          </div>

          {chat.thread ? (
            <>
              <span ref={menuAnchor}>
                <IconButton
                  size={34}
                  variant="ghost"
                  icon={<MoreHorizontal size={17} aria-hidden />}
                  label={t('chat.sidebar.menu')}
                  onClick={() => setMenuOpen(true)}
                  data-testid="chat-header-menu"
                />
              </span>
              <Menu
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                anchorRef={menuAnchor}
                width={190}
                ariaLabel={t('chat.sidebar.menu')}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); setTitleValue(title); setEditingTitle(true); }}
                  style={MENU_ROW}
                  data-testid="chat-header-rename"
                >
                  <Pencil size={15} aria-hidden />
                  {t('common.actions.rename')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}
                  style={{ ...MENU_ROW, color: CT.dangerInk }}
                  data-testid="chat-header-delete"
                >
                  <Trash2 size={15} aria-hidden />
                  {t('common.actions.delete')}
                </button>
              </Menu>
            </>
          ) : null}
        </header>

        <AiDisclaimer style={{ margin: '10px 16px 0' }} />

        {chat.error ? (
          <ErrorState error={chat.error} title={t('chat.error.title')} onRetry={chat.reloadThreads} fill />
        ) : caseError ? (
          <ErrorState error={caseError} onRetry={reloadCase} fill />
        ) : chat.loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner label={t('common.labels.loading')} />
          </div>
        ) : empty ? (
          <SuggestedPrompts name={name} caseRecord={caseRecord} onPick={send} />
        ) : (
          <MessageList
            messages={chat.messages}
            reveal={chat.reveal}
            confidence={chat.answer?.confidence}
            sending={chat.sending}
            hasOlder={chat.hasOlder}
            loadingOlder={chat.loadingOlder}
            onLoadOlder={chat.loadOlder}
            onRetry={chat.retry}
            onRegenerate={chat.regenerate}
          />
        )}

        <FollowUps items={chat.answer?.followUps} onPick={send} disabled={chat.sending} />

        <Composer
          value={chat.draft}
          onChange={chat.setDraft}
          onSend={send}
          onStop={chat.stopReveal}
          sending={chat.sending}
          revealing={chat.revealing}
          offline={offline}
          outOfCredits={outOfCredits}
          notice={chat.notice}
          onTopUp={onTopUp}
          focusSignal={focusSignal}
        />
      </main>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('chat.thread.deleteTitle')}
        description={t('chat.thread.deleteBody')}
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={async () => {
          if (chat.thread) await remove(chat.thread.id);
          setConfirmDelete(false);
        }}
        data-testid="chat-delete-confirm"
        confirmTestId="chat-delete-confirm-confirm"
        cancelTestId="chat-delete-confirm-cancel"
      />
    </div>
  );
}

const MENU_ROW = {
  display: 'flex', alignItems: 'center', gap: 8,
  width: '100%', padding: '10px 12px',
  background: 'transparent', border: 'none', font: 'inherit',
  fontSize: 13, cursor: 'pointer', textAlign: 'start', color: 'inherit',
};
